/**
 * FindingsCard — the engine's findings for one expediente: hard failures
 * (rejection reasons) followed by gray-zone flags (what routed it to review).
 * Moved verbatim from the old automated-decision card; the effective
 * disposition now lives in the VerdictBanner.
 */

import { Alert, Box, Card, CardContent, Chip, Stack, Typography } from "@mui/material";

import { FAILURE_CODE_LABEL, FLAG_CODE_LABEL, labelOrCode } from "../../api/labels";
import type { FinalDecisionCompact, Flag } from "../../api/client";
import { PRIORITY_LABEL } from "../../components/decisionChips";
import { ineValidationPendingDetail } from "./identification";

export function FindingsCard({
  automated,
  singleIdentification = false,
}: {
  automated: FinalDecisionCompact;
  /** ADR 0007 / #275: one identification file filled both INE slots (from the
   * documents listing). Re-frames the INE-pending motivo in domain terms; with
   * two distinct documents every flag renders exactly as before. */
  singleIdentification?: boolean;
}) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          {/* Failures (hard) */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Fallas ({automated.failures.length})
            </Typography>
            {automated.failures.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Sin fallas.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {automated.failures.map((f, i) => {
                  const label = labelOrCode(FAILURE_CODE_LABEL, f.code);
                  // The engine detail often restates the label ("INE vencida.
                  // Año de vigencia: 2025"); strip the duplicated prefix so it
                  // reads "<label> — <specifics>" instead of repeating itself.
                  let detail = f.detail?.trim() ?? "";
                  if (detail.toLowerCase().startsWith(label.toLowerCase())) {
                    detail = detail.slice(label.length).replace(/^[\s.:;,—–-]+/, "").trim();
                  }
                  return (
                    <Alert
                      key={`${f.code}-${i}`}
                      severity="error"
                      icon={false}
                      sx={{ py: 0.5 }}
                    >
                      <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                        {label}
                      </Typography>
                      {detail ? ` — ${detail}` : ""}
                    </Alert>
                  );
                })}
              </Stack>
            )}
          </Box>

          {/* Flags (gray-zone) */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Indicadores ({automated.flags.length})
            </Typography>
            {automated.flags.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Sin indicadores.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {automated.flags.map((flag: Flag, i) => {
                  // INE-pending under a single-source identification: the engine's
                  // "reverso no disponible" wording contradicts the visible reverso
                  // card, so the domain framing is prepended — the engine motivo
                  // stays verbatim (#275). Every other flag — and this one, with
                  // two distinct documents — shows the engine detail untouched.
                  const detail =
                    flag.code === "IDEN_VALIDATION_PENDING" ||
                    flag.code === "INE_VALIDATION_PENDING"
                      ? ineValidationPendingDetail(flag.detail, singleIdentification)
                      : flag.detail;
                  return (
                    <Box
                      key={`${flag.code}-${i}`}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 1,
                      }}
                    >
                      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {labelOrCode(FLAG_CODE_LABEL, flag.code)}
                        </Typography>
                        <Chip
                          label={PRIORITY_LABEL[flag.priority]}
                          color={flag.priority === "HIGH" ? "error" : "default"}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                      {detail && (
                        <Typography variant="caption" color="text.secondary">
                          {detail}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
